import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  fetchNotificationEvents,
  updateNotificationEvent,
  fetchEventRouting,
  updateEventRouting,
  fetchNotificationAppRoles,
  fetchNotificationRoleAssignments,
  fetchMobileCatalog,
} from '../../service/api/notification-service';
import { usePermission } from '../../core/utils/PermissionContext';
import { all_routes } from '../../Router/all_routes';
import { showToast } from 'Core/redux/toast/slice';
import './notifications.css';

const PRIORITY_OPTIONS = [
  { value: 1, label: 'Low' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'High' },
  { value: 4, label: 'Critical' },
];

/** 0=None, 1=Acknowledge, 2=Interactive (Approve/Reject — actions TBD). */
const INTERACTION_OPTIONS = [
  { value: 0, label: 'Non-interactive' },
  { value: 1, label: 'Acknowledge' },
  { value: 2, label: 'Interactive' },
];

function priorityLabel(value) {
  return PRIORITY_OPTIONS.find(p => p.value === Number(value))?.label || String(value);
}

function groupTemplateVariables(variables) {
  const groups = [];
  const byName = new Map();
  (variables || []).forEach(variable => {
    const name = String(variable.group || variable.Group || '').trim();
    if (!byName.has(name)) {
      byName.set(name, []);
      groups.push(name);
    }
    byName.get(name).push(variable);
  });
  return groups.map(name => ({ name, items: byName.get(name) }));
}

function interactionLabel(value) {
  return INTERACTION_OPTIONS.find(p => p.value === Number(value))?.label || String(value);
}

function normalizeInteractionType(evt) {
  if (evt.interactionType !== undefined && evt.interactionType !== null) {
    return Number(evt.interactionType);
  }
  // Legacy rows before InteractionType existed.
  return evt.requiresAck ? 1 : 0;
}

const ROLE_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    height: 'auto',
    minHeight: 40,
    alignItems: 'flex-start',
  }),
  valueContainer: (base) => ({
    ...base,
    flexWrap: 'wrap',
    maxHeight: 160,
    overflowY: 'auto',
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: '100%',
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: 'normal',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
};

function mergeEmailList(current, incoming) {
  const ordered = String(current || '')
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);
  const seen = new Set(ordered.map(e => e.toLowerCase()));
  let added = 0;
  for (const raw of incoming || []) {
    const email = String(raw || '').trim();
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    ordered.push(email);
    added += 1;
  }
  return { value: ordered.join(', '), added };
}

const emptyDraft = () => ({
  displayName: '',
  category: '',
  description: '',
  defaultPriority: 2,
  interactionType: 0,
  channelInApp: true,
  channelEmail: false,
  channelSms: false,
  channelPush: false,
  pushModuleId: '',
  pushMenuId: '',
  pushPageId: '',
  messageTemplate: '',
  emailSubjectTemplate: '',
  emailBodyTemplate: '',
  emailUrl: '',
  emailToList: '',
  isActive: true,
});

const ROUTING_ALL = 'All';

function categoryOptionsFromRouting(routing) {
  const groups = Array.isArray(routing?.roleGroups) ? routing.roleGroups : [];
  const options = [{ value: ROUTING_ALL, label: 'All categories' }];
  groups.forEach(group => {
    const category = String(group?.category || '').trim();
    if (category) options.push({ value: category, label: category });
  });
  return options;
}

function roleOptionsForCategory(routing, category) {
  const groups = Array.isArray(routing?.roleGroups) ? routing.roleGroups : [];
  if (!category || category === ROUTING_ALL) {
    return groups.map(group => ({
      label: group.category,
      options: (group.roles || []).map(role => ({
        value: role,
        label: role,
        category: group.category,
      })),
    }));
  }
  const group = groups.find(g => g.category === category);
  return (group?.roles || []).map(role => ({
    value: role,
    label: role,
    category,
  }));
}

function flattenRoleOptions(options) {
  return (options || []).flatMap(option => (option.options ? option.options : [option]));
}

function categoryForRole(routing, role) {
  const groups = Array.isArray(routing?.roleGroups) ? routing.roleGroups : [];
  const match = groups.find(g =>
    (g.roles || []).some(item => String(item).toLowerCase() === String(role).toLowerCase()),
  );
  return match?.category || '';
}

function formatChannels(evt) {
  const parts = [];
  if (evt.channelInApp !== false) parts.push('In-app');
  if (evt.channelEmail) parts.push('Email');
  if (evt.channelSms) parts.push('SMS');
  if (evt.channelPush) parts.push('Push');
  return parts.length ? parts.join(', ') : '—';
}

function formatPushTarget(evt) {
  if (!evt.channelPush) return '—';
  const moduleLabel = evt.pushModuleCode || evt.pushModuleDisplayName;
  const menuLabel = evt.pushMenuDisplayName || evt.pushMenuCode;
  const pageLabel = evt.pushPageDisplayName || evt.pushPageCode;
  const parts = [moduleLabel, menuLabel, pageLabel].filter(Boolean);
  if (parts.length) return parts.join(' / ');
  return 'Not set';
}

function sameCatalogId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return Number(a) === Number(b);
}

function moduleIdMatchingCategory(catalog, category) {
  const code = String(category || '').trim().toLowerCase();
  if (!code) return '';
  const aliases = { ppms: 'pms' };
  const normalized = aliases[code] || code;
  const match = (catalog?.modules || []).find(m => {
    const moduleCode = String(m.moduleCode || '').toLowerCase();
    return moduleCode === normalized || moduleCode === code;
  });
  return match?.moduleId || '';
}

const NotificationEventsAdminView = () => {
  const dispatch = useDispatch();
  // Was hardcoded to APP_Admin / APP_Super_Admin; now granted in Access Control.
  const { can } = usePermission();
  const canAdmin = can('ADMIN.NOTIFICATION_EVENTS.EDIT');

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  /** message | emailSubject | emailBody — chips insert into the last focused template field. */
  const [activeTemplate, setActiveTemplate] = useState('message');
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleEmailOptions, setRoleEmailOptions] = useState([]);
  const [selectedRoleEmails, setSelectedRoleEmails] = useState([]);
  const [loadingRoleEmails, setLoadingRoleEmails] = useState(false);
  const [routing, setRouting] = useState(null);
  const [routingLoaded, setRoutingLoaded] = useState(false);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState(null);
  const [routingCategory, setRoutingCategory] = useState(ROUTING_ALL);
  const [selectedRoutingRoles, setSelectedRoutingRoles] = useState([]);
  const routingLoadIdRef = useRef(0);
  const [mobileCatalog, setMobileCatalog] = useState({ modules: [] });
  const [mobileCatalogLoading, setMobileCatalogLoading] = useState(false);
  const [mobileCatalogError, setMobileCatalogError] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotificationEvents();
      setEvents(data);
    } catch (err) {
      console.error('Failed to load notification events', err);
      setError(err.message || 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMobileCatalog = useCallback(async () => {
    setMobileCatalogLoading(true);
    setMobileCatalogError(null);
    try {
      const data = await fetchMobileCatalog();
      setMobileCatalog({
        modules: Array.isArray(data?.modules) ? data.modules : [],
      });
    } catch (err) {
      console.error('Failed to load mobile catalog', err);
      setMobileCatalog({ modules: [] });
      setMobileCatalogError(err.message || 'Failed to load mobile modules');
    } finally {
      setMobileCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAdmin) loadEvents();
  }, [canAdmin, loadEvents]);

  useEffect(() => {
    if (canAdmin) loadMobileCatalog();
  }, [canAdmin, loadMobileCatalog]);

  useEffect(() => {
    if (!canAdmin || !editing || !draft.channelEmail || roleOptions.length > 0) {
      return undefined;
    }

    let cancelled = false;
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const appRoles = await fetchNotificationAppRoles();
        if (cancelled) return;
        const options = appRoles
          .map(role => {
            const value = (role.value || role.displayName || '').trim();
            if (!value) return null;
            return {
              value,
              label: (role.displayName || role.value || value).trim(),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setRoleOptions(options);
      } catch (err) {
        console.error('Failed to load app roles', err);
        if (!cancelled) setRoleOptions([]);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [canAdmin, editing, draft.channelEmail, roleOptions.length]);

  if (!canAdmin) {
    return <Navigate to={all_routes.notifications} replace />;
  }

  const openEdit = (evt) => {
    setEditing(evt);
    setDraft({
      displayName: evt.displayName || '',
      category: evt.category || '',
      description: evt.description || '',
      defaultPriority: evt.defaultPriority ?? 2,
      interactionType: normalizeInteractionType(evt),
      channelInApp: evt.channelInApp !== false,
      channelEmail: !!evt.channelEmail,
      channelSms: !!evt.channelSms,
      channelPush: !!evt.channelPush,
      pushModuleId: evt.pushModuleId || (evt.channelPush
        ? moduleIdMatchingCategory(mobileCatalog, evt.category)
        : ''),
      pushMenuId: evt.pushMenuId || '',
      pushPageId: evt.pushPageId || '',
      messageTemplate: evt.messageTemplate || '',
      emailSubjectTemplate: evt.emailSubjectTemplate || '',
      emailBodyTemplate: evt.emailBodyTemplate || '',
      emailUrl: evt.emailUrl || '',
      emailToList: evt.emailToList || '',
      isActive: !!evt.isActive,
    });
    setSelectedRoles([]);
    setRoleEmailOptions([]);
    setSelectedRoleEmails([]);
    setSaveError(null);
    setActiveTemplate('message');
    setRouting(null);
    setRoutingLoaded(false);
    setRoutingLoading(true);
    setRoutingError(null);
    setSelectedRoutingRoles([]);
    setRoutingCategory(evt.category || ROUTING_ALL);

    const loadId = routingLoadIdRef.current + 1;
    routingLoadIdRef.current = loadId;
    fetchEventRouting(evt.eventId)
      .then(data => {
        if (routingLoadIdRef.current !== loadId) return;
        setRouting(data);
        setSelectedRoutingRoles(Array.isArray(data?.roles) ? data.roles : []);
        const groups = Array.isArray(data?.roleGroups) ? data.roleGroups : [];
        const eventCat = data?.eventCategory || evt.category;
        const hasGroup = groups.some(g => g.category === eventCat);
        setRoutingCategory(hasGroup ? eventCat : ROUTING_ALL);
        setRoutingLoaded(true);
        setRoutingError(null);
      })
      .catch(err => {
        if (routingLoadIdRef.current !== loadId) return;
        setRoutingError(err.message || 'Failed to load routing rules');
        setRoutingLoaded(false);
      })
      .finally(() => {
        if (routingLoadIdRef.current !== loadId) return;
        setRoutingLoading(false);
      });
  };

  const closeEdit = () => {
    routingLoadIdRef.current += 1;
    setEditing(null);
    setDraft(emptyDraft());
    setSelectedRoles([]);
    setRoleEmailOptions([]);
    setSelectedRoleEmails([]);
    setSaveError(null);
    setActiveTemplate('message');
    setRouting(null);
    setRoutingLoaded(false);
    setRoutingLoading(false);
    setRoutingError(null);
    setSelectedRoutingRoles([]);
    setRoutingCategory(ROUTING_ALL);
  };

  const insertToken = (key) => {
    if (!key) return;
    const token = `{{${key}}}`;
    const field =
      activeTemplate === 'emailSubject'
        ? 'emailSubjectTemplate'
        : activeTemplate === 'emailBody'
          ? 'emailBodyTemplate'
          : 'messageTemplate';
    setDraft(d => {
      const current = d[field] || '';
      const spacer = current && !/\s$/.test(current) ? ' ' : '';
      return { ...d, [field]: `${current}${spacer}${token}` };
    });
  };

  const handleRoutingCategoryChange = option => {
    setRoutingCategory(option?.value || ROUTING_ALL);
  };

  const handleRoutingRolesChange = value => {
    const next = (value || []).map(option => option.value);
    if (routingCategory === ROUTING_ALL) {
      setSelectedRoutingRoles(next);
      return;
    }
    const current = new Set(
      flattenRoleOptions(roleOptionsForCategory(routing, routingCategory)).map(option => option.value),
    );
    const kept = selectedRoutingRoles.filter(role => !current.has(role));
    setSelectedRoutingRoles([...kept, ...next]);
  };

  const handleSelectAllRoutingRoles = checked => {
    const currentValues = flattenRoleOptions(
      roleOptionsForCategory(routing, routingCategory),
    ).map(option => option.value);
    if (checked) {
      const seen = new Set(selectedRoutingRoles);
      const merged = [...selectedRoutingRoles];
      currentValues.forEach(role => {
        if (!seen.has(role)) {
          seen.add(role);
          merged.push(role);
        }
      });
      setSelectedRoutingRoles(merged);
    } else {
      const drop = new Set(currentValues);
      setSelectedRoutingRoles(selectedRoutingRoles.filter(role => !drop.has(role)));
    }
  };

  const handleRemoveRoutingRole = role => {
    setSelectedRoutingRoles(prev => prev.filter(item => item !== role));
  };

  const handleLoadEmailsForRoles = async () => {
    if (!selectedRoles.length) {
      dispatch(showToast({ message: 'Select at least one role.', type: 'info' }));
      return;
    }

    setLoadingRoleEmails(true);
    setSelectedRoleEmails([]);
    try {
      const results = await Promise.all(
        selectedRoles.map(role => fetchNotificationRoleAssignments(role.value)),
      );
      const assignments = results.flat();
      const byEmail = new Map();
      assignments.forEach(a => {
          const email = String(a.userEmail || '').trim();
          if (!email) return;
          const key = email.toLowerCase();
          if (byEmail.has(key)) return;
          const name = String(a.userDisplayName || '').trim();
          const roleName = String(a.roleName || '').trim();
          byEmail.set(key, {
            value: email,
            label: name ? `${name} (${email})` : email,
            roleName,
          });
        });

      const options = Array.from(byEmail.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setRoleEmailOptions(options);
      if (!options.length) {
        dispatch(showToast({
          message: 'No emails found for the selected roles.',
          type: 'warning',
        }));
      }
    } catch (err) {
      setRoleEmailOptions([]);
      dispatch(showToast({
        message: err.message || 'Failed to load emails for roles.',
        type: 'danger',
      }));
    } finally {
      setLoadingRoleEmails(false);
    }
  };

  const handleAddSelectedEmails = () => {
    if (!selectedRoleEmails.length) {
      dispatch(showToast({ message: 'Select at least one email.', type: 'info' }));
      return;
    }

    const emails = selectedRoleEmails.map(o => o.value);
    const { value, added } = mergeEmailList(draft.emailToList, emails);
    setDraft(d => ({ ...d, emailToList: value }));
    if (added === 0) {
      dispatch(showToast({
        message: 'Those emails are already in the list.',
        type: 'warning',
      }));
    } else {
      dispatch(showToast({
        message: `Added ${added} email(s) to the list.`,
        type: 'success',
      }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing?.eventId) return;

    const interactionType = Number(draft.interactionType);
    if (draft.channelPush && (!draft.pushModuleId || !draft.pushMenuId || !draft.pushPageId)) {
      setSaveError('Select a mobile module, menu, and page when Mobile push is on.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateNotificationEvent(editing.eventId, {
        displayName: draft.displayName.trim(),
        category: draft.category.trim(),
        description: draft.description?.trim() || null,
        defaultPriority: Number(draft.defaultPriority),
        interactionType,
        requiresAck: interactionType === 1,
        channelInApp: !!draft.channelInApp,
        channelEmail: !!draft.channelEmail,
        channelSms: !!draft.channelSms,
        channelPush: !!draft.channelPush,
        pushModuleId: draft.channelPush ? Number(draft.pushModuleId) || null : null,
        pushMenuId: draft.channelPush ? Number(draft.pushMenuId) || null : null,
        pushPageId: draft.channelPush ? Number(draft.pushPageId) || null : null,
        messageTemplate: draft.messageTemplate,
        emailSubjectTemplate: draft.emailSubjectTemplate,
        emailBodyTemplate: draft.emailBodyTemplate,
        emailUrl: draft.emailUrl,
        emailToList: draft.emailToList,
        isActive: !!draft.isActive,
      });
      setEvents(prev =>
        prev.map(item => (item.eventId === updated.eventId ? updated : item)),
      );
      if (routingLoaded) {
        try {
          await updateEventRouting(editing.eventId, selectedRoutingRoles);
        } catch (routingErr) {
          setSaveError(
            `Event settings saved, but in-app recipients failed: ${
              routingErr.message || 'unknown error'
            }`,
          );
          dispatch(showToast({
            message: 'Event saved, but recipients were not updated.',
            type: 'warning',
          }));
          return;
        }
      }
      closeEdit();
      dispatch(showToast({ message: 'Event settings saved successfully!', type: 'success' }));
    } catch (err) {
      setSaveError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const routingCategoryOptions = categoryOptionsFromRouting(routing);
  const routingRoleOptions = roleOptionsForCategory(routing, routingCategory);
  const routingRoleFlat = flattenRoleOptions(routingRoleOptions);
  const selectedRoutingRoleOptions = routingRoleFlat.filter(option =>
    selectedRoutingRoles.includes(option.value),
  );
  const allRoutingRolesSelected =
    routingRoleFlat.length > 0
    && routingRoleFlat.every(option => selectedRoutingRoles.includes(option.value));
  const selectedRoutingCategoryOption =
    routingCategoryOptions.find(option => option.value === routingCategory)
    || routingCategoryOptions[0];
  const selectMenuPortal = typeof document !== 'undefined' ? document.body : null;
  const pushModuleOptions = (mobileCatalog.modules || []).map(module => ({
    value: module.moduleId,
    label: `${module.moduleCode} — ${module.displayName}`,
    moduleCode: module.moduleCode,
  }));
  const selectedPushModule = (mobileCatalog.modules || []).find(
    module => sameCatalogId(module.moduleId, draft.pushModuleId),
  );
  const pushMenuOptions = (selectedPushModule?.menus || []).map(menu => ({
    value: menu.menuId,
    label: menu.displayName,
    menuCode: menu.menuCode,
  }));
  const selectedPushMenu = (selectedPushModule?.menus || []).find(
    menu => sameCatalogId(menu.menuId, draft.pushMenuId),
  );
  const pushPageOptions = (selectedPushMenu?.pages || [])
    .filter(page => page.isPushTarget || sameCatalogId(page.pageId, draft.pushPageId))
    .map(page => ({
      value: page.pageId,
      label: page.displayName,
      mobileAreaName: page.mobileAreaName,
      mobilePageName: page.mobilePageName,
    }));
  const selectedPushModuleOption =
    pushModuleOptions.find(option => sameCatalogId(option.value, draft.pushModuleId)) || null;
  const selectedPushMenuOption =
    pushMenuOptions.find(option => sameCatalogId(option.value, draft.pushMenuId)) || null;
  const selectedPushPageOption =
    pushPageOptions.find(option => sameCatalogId(option.value, draft.pushPageId)) || null;

  return (
    <div className="page-wrapper">
      <div className="content notifications-page notification-events-admin">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Notification Events</h4>
              <h6>
                Admin catalog settings (active, interaction type, priority). Event codes are fixed by
                Chemplast API publish calls — create is not available here.
              </h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={loadEvents}
              disabled={loading}
            >
              <FeatherIcon icon="refresh-cw" size={16} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="card notifications-card">
          <div className="card-body">
            {loading && <p className="text-muted mb-0">Loading events…</p>}
            {!loading && events.length === 0 && !error && (
              <p className="text-muted mb-0">No events found. Seed Notification_Event in the database first.</p>
            )}
            {!loading && events.length > 0 && (
              <div className="table-responsive">
                <table className="table noti-events-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Event code</th>
                      <th>Display name</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Interaction</th>
                      <th>Channels</th>
                      <th>Push target</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(evt => (
                      <tr key={evt.eventId}>
                        <td>
                          <code className="noti-event-code">{evt.eventCode}</code>
                        </td>
                        <td>{evt.displayName}</td>
                        <td>
                          <span className="badge bg-light text-dark">{evt.category}</span>
                        </td>
                        <td>{priorityLabel(evt.defaultPriority)}</td>
                        <td>{interactionLabel(normalizeInteractionType(evt))}</td>
                        <td>{formatChannels(evt)}</td>
                        <td>{formatPushTarget(evt)}</td>
                        <td>
                          {evt.isActive ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Off</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openEdit(evt)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {editing && (
          <div className="noti-event-edit-overlay" role="dialog" aria-modal="true">
            <div className="noti-event-edit-panel card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Edit event</h5>
                    <code className="noti-event-code">{editing.eventCode}</code>
                  </div>
                  <button type="button" className="btn btn-sm btn-light" onClick={closeEdit}>
                    <FeatherIcon icon="x" size={16} />
                  </button>
                </div>

                <form onSubmit={handleSave}>
                  <div className="mb-3">
                    <label className="form-label">Display name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.displayName}
                      onChange={e => setDraft(d => ({ ...d, displayName: e.target.value }))}
                      required
                      maxLength={250}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Category</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.category}
                      onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
                      required
                      maxLength={50}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={draft.description}
                      onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                      maxLength={500}
                    />
                  </div>
                  <div className="mb-3 noti-routing-section">
                    <label className="form-label">In-app recipients (user groups)</label>
                    <div className="form-text mb-2">
                      Choose a category, then the user groups to notify.
                    </div>
                    {routingLoading && (
                      <p className="text-muted small mb-2">Loading routing rules…</p>
                    )}
                    {routingError && (
                      <div className="alert alert-warning py-2" role="alert">
                        {routingError}. Event settings can still be saved; recipients will not
                        change.
                      </div>
                    )}
                    {routing?.hasFromPayload && (
                      <div className="alert alert-info py-2" role="status">
                        This event also notifies people from the publish payload (for example
                        change-request assignees). That rule is kept and is not edited here.
                      </div>
                    )}
                    {routing?.hasExplicitUsers && (
                      <div className="alert alert-info py-2" role="status">
                        This event also has named-user rules. Those are kept and are not edited
                        here.
                      </div>
                    )}
                    <div className="row g-2 align-items-end mb-2">
                      <div className="col-md-4">
                        <label className="form-label" htmlFor="noti-routing-category">
                          Category
                        </label>
                        <Select
                          inputId="noti-routing-category"
                          classNamePrefix="react-select"
                          options={routingCategoryOptions}
                          value={selectedRoutingCategoryOption}
                          onChange={handleRoutingCategoryChange}
                          isSearchable={false}
                          isDisabled={routingLoading || !routing}
                          styles={ROLE_SELECT_STYLES}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                        />
                      </div>
                      <div className="col-md-8">
                        <div className="d-flex justify-content-between align-items-center">
                          <label className="form-label mb-0" htmlFor="noti-routing-roles">
                            User groups
                          </label>
                          <div className="form-check mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="noti-routing-select-all"
                              checked={allRoutingRolesSelected}
                              onChange={e => handleSelectAllRoutingRoles(e.target.checked)}
                              disabled={
                                routingLoading || !routing || routingRoleFlat.length === 0
                              }
                            />
                            <label className="form-check-label" htmlFor="noti-routing-select-all">
                              Select all
                            </label>
                          </div>
                        </div>
                        <div className="noti-role-select mt-1">
                          <Select
                            inputId="noti-routing-roles"
                            isMulti
                            isLoading={routingLoading}
                            options={routingRoleOptions}
                            value={selectedRoutingRoleOptions}
                            onChange={handleRoutingRolesChange}
                            placeholder={
                              routingLoading
                                ? 'Loading user groups...'
                                : routingCategory === ROUTING_ALL
                                  ? 'Select user groups...'
                                  : `Select ${routingCategory} user groups...`
                            }
                            isDisabled={routingLoading || !routing}
                            classNamePrefix="react-select"
                            styles={ROLE_SELECT_STYLES}
                            menuPortalTarget={selectMenuPortal}
                            menuPosition="fixed"
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => 'No user groups in this category'}
                          />
                        </div>
                      </div>
                    </div>
                    {selectedRoutingRoles.length > 0 && (
                      <div className="noti-routing-chips">
                        {selectedRoutingRoles.map(role => {
                          const roleCategory = categoryForRole(routing, role);
                          return (
                            <span key={role} className="noti-routing-chip">
                              {roleCategory ? (
                                <span className="noti-routing-chip-cat">{roleCategory}</span>
                              ) : null}
                              {role}
                              <button
                                type="button"
                                className="noti-routing-chip-remove"
                                aria-label={`Remove ${role}`}
                                onClick={() => handleRemoveRoutingRole(role)}
                              >
                                <FeatherIcon icon="x" size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {!routingLoading && routing && selectedRoutingRoles.length === 0 && (
                      <div className="form-text">
                        No user groups selected. In-app notifications will only go to payload or
                        named-user rules, if any.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message template (in-app)</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={draft.messageTemplate}
                      onFocus={() => setActiveTemplate('message')}
                      onChange={e => setDraft(d => ({ ...d, messageTemplate: e.target.value }))}
                      maxLength={1000}
                      placeholder="In-app inbox body — use chips below for payload tokens"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label d-block">Delivery channels</label>
                    <div className="form-text mb-2">
                      Email / SMS / Push senders are not implemented yet. Turning Email on shows
                      subject and body templates to store for later.
                    </div>
                    {[
                      { id: 'channelInApp', key: 'channelInApp', label: 'In-app' },
                      { id: 'channelEmail', key: 'channelEmail', label: 'Email' },
                      { id: 'channelSms', key: 'channelSms', label: 'SMS' },
                      { id: 'channelPush', key: 'channelPush', label: 'Mobile push' },
                    ].map(ch => (
                      <div className="form-check form-switch mb-1" key={ch.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={ch.id}
                          checked={!!draft[ch.key]}
                          onChange={e => {
                            const checked = e.target.checked;
                            if (ch.key !== 'channelPush') {
                              setDraft(d => ({ ...d, [ch.key]: checked }));
                              return;
                            }
                            setDraft(d => ({
                              ...d,
                              channelPush: checked,
                              pushModuleId: checked
                                ? (d.pushModuleId
                                  || moduleIdMatchingCategory(mobileCatalog, d.category)
                                  || '')
                                : '',
                              pushMenuId: checked ? d.pushMenuId : '',
                              pushPageId: checked ? d.pushPageId : '',
                            }));
                          }}
                        />
                        <label className="form-check-label" htmlFor={ch.id}>
                          {ch.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {draft.channelPush && (
                    <div className="mb-3">
                      <label className="form-label d-block">Mobile push target</label>
                      <div className="form-text mb-2">
                        Choose the mobile module, menu, and page this notification should open.
                      </div>
                      {mobileCatalogLoading && (
                        <p className="text-muted small mb-2">Loading mobile modules…</p>
                      )}
                      {mobileCatalogError && (
                        <div className="alert alert-warning py-2" role="alert">
                          {mobileCatalogError}. Seed Mobile_Module / Mobile_Menu / Mobile_Page, then refresh.
                        </div>
                      )}
                      <div className="row g-2">
                        <div className="col-md-4">
                          <label className="form-label" htmlFor="noti-push-module">
                            Module
                          </label>
                          <Select
                            inputId="noti-push-module"
                            classNamePrefix="react-select"
                            options={pushModuleOptions}
                            value={selectedPushModuleOption}
                            onChange={option =>
                              setDraft(d => ({
                                ...d,
                                pushModuleId: option?.value || '',
                                pushMenuId: '',
                                pushPageId: '',
                              }))
                            }
                            placeholder="Select module…"
                            isDisabled={mobileCatalogLoading || !pushModuleOptions.length}
                            styles={ROLE_SELECT_STYLES}
                            menuPortalTarget={selectMenuPortal}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label" htmlFor="noti-push-menu">
                            Menu
                          </label>
                          <Select
                            inputId="noti-push-menu"
                            classNamePrefix="react-select"
                            options={pushMenuOptions}
                            value={selectedPushMenuOption}
                            onChange={option =>
                              setDraft(d => ({
                                ...d,
                                pushMenuId: option?.value || '',
                                pushPageId: '',
                              }))
                            }
                            placeholder={
                              draft.pushModuleId ? 'Select menu…' : 'Select a module first'
                            }
                            isDisabled={!draft.pushModuleId || !pushMenuOptions.length}
                            styles={ROLE_SELECT_STYLES}
                            menuPortalTarget={selectMenuPortal}
                            menuPosition="fixed"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label" htmlFor="noti-push-page">
                            Page
                          </label>
                          <Select
                            inputId="noti-push-page"
                            classNamePrefix="react-select"
                            options={pushPageOptions}
                            value={selectedPushPageOption}
                            onChange={option =>
                              setDraft(d => ({ ...d, pushPageId: option?.value || '' }))
                            }
                            placeholder={
                              draft.pushMenuId ? 'Select page…' : 'Select a menu first'
                            }
                            isDisabled={!draft.pushMenuId || !pushPageOptions.length}
                            styles={ROLE_SELECT_STYLES}
                            menuPortalTarget={selectMenuPortal}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>
                      {(selectedPushMenuOption || selectedPushPageOption) && (
                        <div className="form-text mt-2">
                          Mobile key:{' '}
                          {[
                            selectedPushModuleOption?.moduleCode,
                            selectedPushMenuOption?.label,
                            selectedPushPageOption?.label,
                          ].filter(Boolean).join(' / ')}
                        </div>
                      )}
                    </div>
                  )}
                  {draft.channelEmail && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Email subject template</label>
                        <input
                          type="text"
                          className="form-control"
                          value={draft.emailSubjectTemplate}
                          onFocus={() => setActiveTemplate('emailSubject')}
                          onChange={e =>
                            setDraft(d => ({ ...d, emailSubjectTemplate: e.target.value }))
                          }
                          maxLength={250}
                          placeholder="e.g. Change request {{crNumber}} created"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email body template</label>
                        <textarea
                          className="form-control"
                          rows={5}
                          value={draft.emailBodyTemplate}
                          onFocus={() => setActiveTemplate('emailBody')}
                          onChange={e =>
                            setDraft(d => ({ ...d, emailBodyTemplate: e.target.value }))
                          }
                          placeholder="Plain-text email body — same payload tokens as in-app"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email URL</label>
                        <input
                          type="url"
                          className="form-control"
                          value={draft.emailUrl}
                          onChange={e =>
                            setDraft(d => ({ ...d, emailUrl: e.target.value }))
                          }
                          maxLength={1000}
                          placeholder="HTTP Trigger URL"
                          autoComplete="off"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Email To list</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={draft.emailToList}
                          onChange={e =>
                            setDraft(d => ({ ...d, emailToList: e.target.value }))
                          }
                          placeholder="email1@domain.com, email2@domain.com"
                        />
                        <div className="mt-2">
                          <label className="form-label">Add from roles</label>
                          <div className="noti-role-select-row mb-2">
                            <div className="noti-role-select">
                              <Select
                                isMulti
                                isLoading={rolesLoading}
                                options={roleOptions}
                                value={selectedRoles}
                                onChange={value => {
                                  setSelectedRoles(value || []);
                                  setRoleEmailOptions([]);
                                  setSelectedRoleEmails([]);
                                }}
                                placeholder={rolesLoading ? 'Loading roles...' : 'Filter by role...'}
                                classNamePrefix="react-select"
                                styles={ROLE_SELECT_STYLES}
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                              />
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={handleLoadEmailsForRoles}
                              disabled={loadingRoleEmails || rolesLoading || !selectedRoles.length}
                            >
                              {loadingRoleEmails ? 'Loading...' : 'Load emails'}
                            </button>
                          </div>
                          {(roleEmailOptions.length > 0 || loadingRoleEmails) && (
                            <div className="noti-role-select-row">
                              <div className="noti-role-select">
                                <Select
                                  isMulti
                                  isLoading={loadingRoleEmails}
                                  options={roleEmailOptions}
                                  value={selectedRoleEmails}
                                  onChange={value => setSelectedRoleEmails(value || [])}
                                  placeholder="Select emails to add..."
                                  classNamePrefix="react-select"
                                  styles={ROLE_SELECT_STYLES}
                                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                  menuPosition="fixed"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleAddSelectedEmails}
                                disabled={loadingRoleEmails || !selectedRoleEmails.length}
                              >
                                Add selected
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="mb-3">
                    <label className="form-label d-block">
                      Variables for {editing.eventCode}
                    </label>
                    <div className="form-text mb-2">
                      Click a chip to insert into the{' '}
                      {activeTemplate === 'emailSubject'
                        ? 'email subject'
                        : activeTemplate === 'emailBody'
                          ? 'email body'
                          : 'in-app message'}{' '}
                      template. Line items are not tokens; use header fields only.
                    </div>
                    <div className="noti-token-chip-board">
                      {groupTemplateVariables(editing.availableVariables).map(section => (
                        <div key={section.name || 'tokens'} className="noti-token-chip-group">
                          {section.name ? (
                            <div className="noti-token-chip-group-title">{section.name}</div>
                          ) : null}
                          <div className="noti-token-chips">
                            {section.items.map((variable, index) => (
                              <button
                                key={`${variable.key}-${index}`}
                                type="button"
                                className="noti-token-chip"
                                title={`Insert {{${variable.key}}}`}
                                onClick={() => insertToken(variable.key)}
                              >
                                {variable.label || variable.key}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Default priority</label>
                    <select
                      className="form-select"
                      value={draft.defaultPriority}
                      onChange={e =>
                        setDraft(d => ({ ...d, defaultPriority: Number(e.target.value) }))
                      }
                    >
                      {PRIORITY_OPTIONS.map(p => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Interaction type</label>
                    <select
                      className="form-select"
                      value={draft.interactionType}
                      onChange={e =>
                        setDraft(d => ({ ...d, interactionType: Number(e.target.value) }))
                      }
                    >
                      {INTERACTION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Interactive = Approve/Reject (actions not wired yet). Acknowledge keeps the
                      existing ack workflow.
                    </div>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="evtIsActive"
                      checked={draft.isActive}
                      onChange={e => setDraft(d => ({ ...d, isActive: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="evtIsActive">
                      Event active (off = publish rejected as inactive)
                    </label>
                  </div>

                  {saveError && (
                    <div className="alert alert-danger py-2" role="alert">
                      {saveError}
                    </div>
                  )}

                  <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-light" onClick={closeEdit} disabled={saving}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving || routingLoading}>
                      {saving ? 'Saving…' : routingLoading ? 'Loading recipients…' : 'Save changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationEventsAdminView;
